import React from "react";
import {autobind} from "core-decorators";
import {action, observable} from "mobx";
import {observer} from "mobx-react";
import {Button} from "antd";
import {FaPlay, FaStop} from "react-icons/fa";
import * as Tone from "tone";

import "./AudioButton.scss";

export interface IAudioButtonProps {
  url: string; // audio url
}

@autobind
@observer
export default class AudioButton extends React.Component<IAudioButtonProps> {
  @observable isLoading: boolean = false;
  @observable isPlaying: boolean = false;

  audioContext?: Tone.Context;
  player?: Tone.Player;

  async loadAudio() {
    if (this.player) return;
    this.setIsLoading(true);
    // We need to create a specific Transport to detect when audio is stopped
    this.audioContext = new Tone.Context(Tone.context.rawContext);
    await new Promise<void>(
      (resolve) =>
        (this.player = new Tone.Player({
          url: this.props.url,
          context: this.audioContext,
          onload: resolve,
        })),
    );
    this.audioContext.transport.on("start", (time) =>
      this.audioContext!.draw.schedule(() => this.setIsPlaying(true), time),
    );
    this.audioContext.transport.on("stop", (time) =>
      this.audioContext!.draw.schedule(() => this.setIsPlaying(false), time),
    );
    this.player!.toDestination().sync();
    this.player!.start(0);
    this.setIsLoading(false);
  }

  async play() {
    await this.loadAudio();
    const now = this.audioContext!.now();
    this.audioContext!.transport.start(now);
    this.audioContext!.transport.stop(now + this.player!.buffer.duration);
  }

  onClick() {
    (this.isPlaying ? this.stop : this.play)();
  }

  @action
  setIsLoading(value: boolean) {
    this.isLoading = value;
  }

  @action
  setIsPlaying(value: boolean) {
    this.isPlaying = value;
  }

  stop() {
    this.audioContext!.transport.stop();
  }

  public render() {
    return (
      <Button
        className="AudioButton"
        type="primary"
        shape="circle"
        size="small"
        onClick={this.onClick}
        loading={this.isLoading}
      >
        {this.isLoading ? null : this.isPlaying ? <FaStop /> : <FaPlay />}
      </Button>
    );
  }
}
